import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Dashboard.css';
import { 
  FiShoppingBag, 
  FiTrendingUp, 
  FiBarChart2, 
  FiSettings,
  FiLogOut,
  FiBell,
  FiSearch,
  FiShield,
  FiClock,
  FiDollarSign,
  FiCreditCard,
  FiPieChart,
  FiUsers,
  FiPackage,
  FiTruck,
  FiCheckCircle,
  FiAlertCircle,
  FiArrowUp,
  FiArrowDown,
  FiRefreshCw,
  FiEye,
  FiDownload,
  FiPrinter,
  FiFileText
} from 'react-icons/fi';
import { HiOutlineSparkles, HiOutlineCurrencyDollar } from 'react-icons/hi';
import { FaWallet, FaStore, FaChartLine, FaMoneyBillWave, FaExchangeAlt, FaUsers, FaUser } from 'react-icons/fa';

// Import the new components
import WalletManagement from '../components/WalletManagement';
import BusinessAnalytics from '../components/BusinessAnalytics';
import StoreManagement from '../components/StoreManagement';
import TransactionHistory from '../components/TransactionHistory';
import BulkPayout from '../components/BulkPayout';
import Profile from '../components/Profile';
import EInvoice from '../components/EInvoice';

interface MerchantData {
  DSSN: string;
  business_name: string;
  merchant_id: number;
  business_registration_number: string;
  email: string;
  phone_number: string;
}

interface WalletData {
  usd_balance: number;
  lrd_balance: number;
}

interface Transaction {
  transactionId: number;
  reference: string;
  amount: number;
  currency: string;
  status: string;
  transaction_type: string;
  created_at: string;
  recipient_email?: string;
  sender_email?: string;
}

interface BusinessStats {
  total_sales: number;
  total_sales_lrd: number;
  total_transactions: number;
  average_order_value: number;
  monthly_growth: number;
  pending_orders: number;
  completed_orders: number;
  top_customers: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

const MerchantDashboard: React.FC = () => {
  const { token, merchant, logout } = useAuth();
  const navigate = useNavigate();
  
  const [merchantData, setMerchantData] = useState<MerchantData | null>(null);
  const [wallet, setWallet] = useState<WalletData>({ usd_balance: 0, lrd_balance: 0 });
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [businessStats, setBusinessStats] = useState<BusinessStats>({
    total_sales: 0,
    total_sales_lrd: 0,
    total_transactions: 0,
    average_order_value: 0,
    monthly_growth: 0,
    pending_orders: 0,
    completed_orders: 0,
    top_customers: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<'USD' | 'LRD'>('USD');
  const [showQRCode, setShowQRCode] = useState(false);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'wallet' | 'store' | 'analytics' | 'transactions' | 'bulk' | 'profile' | 'einvoice'>('dashboard');
  const [notification, setNotification] = useState<{ show: boolean; message: string; type: string }>({ show: false, message: '', type: '' });
  
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    fetchMerchantData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  const fetchMerchantData = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    setLoading(true);
    
    try {
      // Fetch merchant profile
      const profileResponse = await axios.get(`${API_URL}/merchants/profile`, {
        signal: controller.signal,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (profileResponse.data.success) {
        setMerchantData(profileResponse.data.data);
      }
      
      // Fetch wallet balance - FIXED: Backend returns { usd_balance, lrd_balance }
      const walletResponse = await axios.get(`${API_URL}/merchants/wallet`, {
        signal: controller.signal,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (walletResponse.data.success) {
        setWallet({
          usd_balance: walletResponse.data.data?.usd_balance || 0,
          lrd_balance: walletResponse.data.data?.lrd_balance || 0
        });
      }
      
      // Fetch business statistics
      const statsResponse = await axios.get(`${API_URL}/merchants/stats`, {
        signal: controller.signal,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (statsResponse.data.success) {
        setBusinessStats(statsResponse.data.data);
      }
      
      // Fetch recent transactions
      const transactionsResponse = await axios.get(`${API_URL}/merchants/transactions`, {
        signal: controller.signal,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (transactionsResponse.data.success) {
        setTransactions(transactionsResponse.data.data.slice(0, 5));
      }
      
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Failed to fetch merchant data:', error);
        showNotification('Failed to load dashboard data', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMerchantData();
    setRefreshing(false);
    showNotification('Dashboard refreshed successfully', 'success');
  };

  const handleLogout = () => {
    logout();
    navigate('/merchant/login');
  };

  const formatCurrency = (amount: number, currency: 'USD' | 'LRD' = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch(status.toLowerCase()) {
      case 'completed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'failed': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // FIXED: Use usd_balance instead of balance
  const walletCards = [
    {
      title: 'USD Balance',
      balance: wallet.usd_balance || 0,
      currency: 'USD',
      icon: <HiOutlineCurrencyDollar />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      symbol: '$'
    },
    {
      title: 'LRD Balance',
      balance: wallet.lrd_balance || 0,
      currency: 'LRD',
      icon: <FaMoneyBillWave />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      symbol: 'L$'
    }
  ];

  const statCards = [
    { 
      title: 'Total Sales (USD)', 
      value: formatCurrency(businessStats.total_sales || 0, 'USD'),
      icon: <FiTrendingUp />, 
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      subtitle: 'Lifetime revenue',
      trend: '+12%'
    },
    { 
      title: 'Total Sales (LRD)', 
      value: formatCurrency(businessStats.total_sales_lrd || 0, 'LRD'),
      icon: <FiBarChart2 />, 
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      subtitle: 'Lifetime revenue',
      trend: '+8%'
    },
    { 
      title: 'Transactions', 
      value: businessStats.total_transactions || 0,
      icon: <FiPackage />, 
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      subtitle: 'Total orders',
      trend: '+23%'
    },
    { 
      title: 'Avg Order Value', 
      value: formatCurrency(businessStats.average_order_value || 0, selectedCurrency),
      icon: <FiPieChart />, 
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      subtitle: 'Per transaction',
      trend: '+5%'
    },
  ];

  const quickActions = [
    { icon: <FaWallet />, label: 'Wallet', action: 'wallet', color: '#667eea' },
    { icon: <FaExchangeAlt />, label: 'Transfer', action: 'transfer', color: '#f093fb' },
    { icon: <FiEye />, label: 'QR Code', action: 'qr_code', color: '#4facfe' },
    { icon: <FiDownload />, label: 'Statement', action: 'statement', color: '#10b981' },
    { icon: <FiPrinter />, label: 'Receipt', action: 'receipt', color: '#f59e0b' },
    { icon: <FiRefreshCw />, label: 'Refresh', action: 'refresh', color: '#a855f7' },
  ];

  const handleQuickAction = (action: string) => {
    switch(action) {
      case 'qr_code':
        setShowQRCode(true);
        setTimeout(() => setShowQRCode(false), 5000);
        break;
      case 'refresh':
        handleRefresh();
        break;
      case 'wallet':
        setActiveTab('wallet');
        showNotification('Opening wallet management...', 'info');
        break;
      case 'transfer':
        setActiveTab('wallet');
        showNotification('Transfer feature - please use wallet section', 'info');
        break;
      case 'statement':
        showNotification('Downloading statement...', 'info');
        break;
      default:
        showNotification(`${action} feature coming soon`, 'info');
    }
  };

  const renderContent = () => {
    switch(activeTab) {
      case 'wallet':
        return <WalletManagement />;
      case 'store':
        return <StoreManagement />;
      case 'analytics':
        return <BusinessAnalytics />;
      case 'transactions':
        return <TransactionHistory />;
      case 'bulk':
        return <BulkPayout />;
      case 'profile':
        return <Profile />;
      case 'einvoice':
        return <EInvoice />;
      default:
        return (
          <>
            {/* Wallet Section */}
            <div className="wallet-section">
              <div className="section-header">
                <h2>
                  <FaWallet className="section-icon" />
                  My Wallet
                </h2>
                <div className="currency-toggle">
                  <button 
                    className={selectedCurrency === 'USD' ? 'active' : ''} 
                    onClick={() => setSelectedCurrency('USD')}
                  >
                    USD
                  </button>
                  <button 
                    className={selectedCurrency === 'LRD' ? 'active' : ''} 
                    onClick={() => setSelectedCurrency('LRD')}
                  >
                    LRD
                  </button>
                </div>
              </div>
              <div className="wallet-grid">
                {walletCards.map((card, index) => (
                  <div key={index} className="wallet-card glass" style={{ background: card.gradient + ', rgba(10, 10, 20, 0.4)' }}>
                    <div className="wallet-icon">{card.icon}</div>
                    <div className="wallet-info">
                      <p className="wallet-title">{card.title}</p>
                      <h3 className="wallet-balance">{card.symbol}{card.balance.toLocaleString()}</h3>
                      <span className="wallet-subtitle">Available balance</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
              {statCards.map((stat, index) => (
                <div key={index} className="stat-card glass" style={{ background: stat.gradient + ', rgba(10, 10, 20, 0.4)' }}>
                  <div className="stat-icon-wrapper">
                    {stat.icon}
                    <div className="stat-trend positive">{stat.trend}</div>
                  </div>
                  <div className="stat-info">
                    <h3>{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</h3>
                    <p>{stat.title}</p>
                    <span className="stat-subtitle">{stat.subtitle}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="quick-actions-section">
              <h2>Quick Actions</h2>
              <div className="quick-actions-grid">
                {quickActions.map((action, index) => (
                  <div
                    key={index}
                    className="quick-action-card glass"
                    onClick={() => handleQuickAction(action.action)}
                  >
                    <div className="quick-action-icon" style={{ color: action.color }}>
                      {action.icon}
                    </div>
                    <span>{action.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Business Analytics */}
            <div className="analytics-section">
              <div className="section-header">
                <h2>
                  <FiTrendingUp className="section-icon" />
                  Business Analytics
                </h2>
                <button className="view-all" onClick={() => setActiveTab('analytics')}>View Details →</button>
              </div>
              <div className="analytics-grid">
                <div className="analytics-card glass">
                  <div className="analytics-header">
                    <FiUsers />
                    <h3>Top Customers</h3>
                  </div>
                  <div className="analytics-value">{businessStats.top_customers || 0}</div>
                  <p>Unique customers this month</p>
                </div>
                <div className="analytics-card glass">
                  <div className="analytics-header">
                    <FiPackage />
                    <h3>Orders</h3>
                  </div>
                  <div className="analytics-stats">
                    <div className="stat">
                      <span className="stat-value">{businessStats.completed_orders || 0}</span>
                      <span className="stat-label">Completed</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{businessStats.pending_orders || 0}</span>
                      <span className="stat-label">Pending</span>
                    </div>
                  </div>
                </div>
                <div className="analytics-card glass">
                  <div className="analytics-header">
                    <FiTrendingUp />
                    <h3>Growth</h3>
                  </div>
                  <div className="analytics-value positive">
                    +{businessStats.monthly_growth || 0}%
                  </div>
                  <p>Month over month</p>
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="recent-transactions glass">
              <div className="activity-header">
                <h2>Recent Transactions</h2>
                <button className="view-all" onClick={() => setActiveTab('transactions')}>
                  View All →
                </button>
              </div>
              <div className="transaction-list">
                {transactions && transactions.length > 0 ? (
                  transactions.map((tx) => (
                    <div key={tx.transactionId} className="transaction-item">
                      <div className="transaction-icon" style={{ background: getStatusColor(tx.status) + '20' }}>
                        {tx.transaction_type === 'payment' ? <FiArrowUp /> : <FiArrowDown />}
                      </div>
                      <div className="transaction-details">
                        <p className="transaction-title">{tx.transaction_type}</p>
                        <p className="transaction-subtitle">Ref: {tx.reference?.slice(0, 8)}...</p>
                        <span className="transaction-time">{new Date(tx.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="transaction-amount">
                        <p className={tx.transaction_type === 'payment' ? 'negative' : 'positive'}>
                          {tx.transaction_type === 'payment' ? '-' : '+'}
                          {formatCurrency(tx.amount, tx.currency as 'USD' | 'LRD')}
                        </p>
                        <span className="transaction-status" style={{ color: getStatusColor(tx.status) }}>
                          {tx.status}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <p>No transactions yet</p>
                  </div>
                )}
              </div>
            </div>
          </>
        );
    }
  };

  if (loading) {
    return (
      <div className="merchant-dashboard">
        <div className="ai-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
          <div className="grid-overlay"></div>
        </div>
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading your business dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="merchant-dashboard">
      {notification.show && (
        <div className={`notification-toast ${notification.type}`}>
          {notification.type === 'success' && <FiCheckCircle />}
          {notification.type === 'error' && <FiAlertCircle />}
          {notification.type === 'info' && <FiShield />}
          <span>{notification.message}</span>
        </div>
      )}

      {showQRCode && (
        <div className="qr-modal">
          <div className="qr-content glass">
            <h3>Merchant QR Code</h3>
            <div className="qr-code">
              <div className="qr-placeholder">
                <FaStore size={60} />
                <span>Scan to Pay</span>
              </div>
            </div>
            <p>{merchantData?.business_name}</p>
            <button onClick={() => setShowQRCode(false)} className="close-qr">Close</button>
          </div>
        </div>
      )}

      <div className="ai-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="grid-overlay"></div>
      </div>

      <aside className="sidebar glass">
        <div className="sidebar-header">
          <div className="logo">
            <img
              src="/logo.png"
              alt="Digital Liberia Logo"
              className="logo-icon"
            />
            <span>Digital Liberia</span>
            <HiOutlineSparkles className="ai-sparkle" />
          </div>
          <div className="ai-badge">Merchant</div>
        </div>

        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} 
            onClick={() => setActiveTab('dashboard')}
          >
            <span className="nav-icon" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
              <FiBarChart2 />
            </span>
            <div className="nav-text">
              <span className="nav-label">Dashboard</span>
              <span className="nav-description">Overview & Analytics</span>
            </div>
          </button>
          <button 
            className={`nav-item ${activeTab === 'wallet' ? 'active' : ''}`} 
            onClick={() => setActiveTab('wallet')}
          >
            <span className="nav-icon" style={{ background: 'linear-gradient(135deg, #f093fb, #f5576c)' }}>
              <FaWallet />
            </span>
            <div className="nav-text">
              <span className="nav-label">Wallet</span>
              <span className="nav-description">Balance & Transfers</span>
            </div>
          </button>
          <button 
            className={`nav-item ${activeTab === 'store' ? 'active' : ''}`} 
            onClick={() => setActiveTab('store')}
          >
            <span className="nav-icon" style={{ background: 'linear-gradient(135deg, #fa709a, #fee140)' }}>
              <FaStore />
            </span>
            <div className="nav-text">
              <span className="nav-label">Store</span>
              <span className="nav-description">Products & Orders</span>
            </div>
          </button>
          <button 
            className={`nav-item ${activeTab === 'analytics' ? 'active' : ''}`} 
            onClick={() => setActiveTab('analytics')}
          >
            <span className="nav-icon" style={{ background: 'linear-gradient(135deg, #a8edea, #fed6e3)' }}>
              <FaChartLine />
            </span>
            <div className="nav-text">
              <span className="nav-label">Analytics</span>
              <span className="nav-description">Business Insights</span>
            </div>
          </button>
          <button 
            className={`nav-item ${activeTab === 'transactions' ? 'active' : ''}`} 
            onClick={() => setActiveTab('transactions')}
          >
            <span className="nav-icon" style={{ background: 'linear-gradient(135deg, #d4fc79, #96e6a1)' }}>
              <FiClock />
            </span>
            <div className="nav-text">
              <span className="nav-label">Transactions</span>
              <span className="nav-description">History & Reports</span>
            </div>
          </button>
          <button 
            className={`nav-item ${activeTab === 'bulk' ? 'active' : ''}`} 
            onClick={() => setActiveTab('bulk')}
          >
            <span className="nav-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #ea580c)' }}>
              <FaUsers />
            </span>
            <div className="nav-text">
              <span className="nav-label">Bulk Payout</span>
              <span className="nav-description">Salary & Mass Payments</span>
            </div>
          </button>
          <button 
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`} 
            onClick={() => setActiveTab('profile')}
          >
            <span className="nav-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
              <FaUser />
            </span>
            <div className="nav-text">
              <span className="nav-label">Profile</span>
              <span className="nav-description">Business Information</span>
            </div>
          </button>
          <button 
            className={`nav-item ${activeTab === 'einvoice' ? 'active' : ''}`} 
            onClick={() => setActiveTab('einvoice')}
          >
            <span className="nav-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}>
              <FiFileText />
            </span>
            <div className="nav-text">
              <span className="nav-label">E-Invoice</span>
              <span className="nav-description">Send & Receive Invoices</span>
            </div>
          </button>
          <button className="nav-item" onClick={() => showNotification('Settings feature coming soon', 'info')}>
            <span className="nav-icon" style={{ background: 'linear-gradient(135deg, #ff9a9e, #fecfef)' }}>
              <FiSettings />
            </span>
            <div className="nav-text">
              <span className="nav-label">Settings</span>
              <span className="nav-description">Preferences</span>
            </div>
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar-glow">
              <div className="user-avatar">
                {merchantData?.business_name?.charAt(0)?.toUpperCase() || 'M'}
              </div>
            </div>
            <div className="user-details">
              <div className="user-name">{merchantData?.business_name || 'Merchant'}</div>
              <div className="user-role">
                <span className="role-dot"></span>
                Business Account
              </div>
            </div>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            <FiLogOut /> Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="top-bar glass">
          <div className="search-bar">
            <FiSearch />
            <input type="text" placeholder="Search orders, customers..." />
          </div>
          <div className="top-actions">
            <button className="notification-btn">
              <FiBell />
              <span className="notification-dot"></span>
            </button>
            <button onClick={handleRefresh} className="refresh-btn" disabled={refreshing}>
              <FiRefreshCw className={refreshing ? 'spin' : ''} />
            </button>
            <div className="ai-status">
              <div className="ai-pulse"></div>
              <span>Live Updates</span>
            </div>
          </div>
        </header>

        <div className="content-area">
          <div className="page-header">
            <div>
              <h1>Welcome back, {merchantData?.business_name?.split(' ')[0] || 'Merchant'}!</h1>
              <p>Manage your business, track sales, and process payments.</p>
            </div>
            <div className="business-info">
              <span className="business-id">ID: {merchantData?.merchant_id}</span>
              <span className="business-status verified">✓ Verified</span>
            </div>
          </div>

          {/* Dynamic Content */}
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

export default MerchantDashboard;
